import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { Add, Delete } from "@mui/icons-material"
import { useCallback, useMemo, useState, useEffect } from "react"
import { LocalStorage } from "src/framework/commons/LocalStorage"
import { InputDecimal3 } from "src/framework/components/forms/inputs/InputDecimal3"
import theme from "src/theme/light"
import { POSICAO_MODULO } from "../../constants"
import { SelectWithImageInOption } from "../../dimensionados/containers/Modulos/containers/SelectWithImageInOption"
import { Grupo, ListagemItem, OptionWithEstrutura, OptionWithImage } from "../../types"
import SemEstrutura from '../../assets/sem-estrutura.png'
import { useObservable } from "src/framework/store/storeUtil"
import "./styles.css"
import { acessouEditarEstruturasHibrido$, HIBRIDO_ERROR_MESSAGES, oldEstruturasHibrido$, useProjetosHibridoStore } from "src/store/vendas/projetos/hibrido/hibrido.store";
import { calcularEstruturas } from "../utils/calcularEstruturas";
import { FormProjetoHibridoType } from "src/store/vendas/projetos/hibrido/hibrido.types";

interface ModalEditarEstruturasProps {
  open: boolean
  segmento?: string
  onClose: () => void
}

const useStyles = makeStyles({
  restoreButtonConfirm: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.getContrastText(theme.palette.error.main),
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    }
  },
  removeFileiraButton: {
    borderColor: theme.palette.error.light,
    color: theme.palette.error.light
  },
  addFileiraButton: {
    backgroundColor: theme.palette.warning.dark,
    color: "white",
    '&:hover': {
      backgroundColor: theme.palette.warning.dark,
    }
  },
  warningText: {
    color: theme.palette.warning.main,
    fontWeight: 600,
  },
  moduleTitle: {
    fontWeight: 600,
  },
  radioButton: {
    marginRight: 14,
  }
})

const renderOption = (option: { url: string, name: string, value: number }): OptionWithImage<number> => {
  const _upload = LocalStorage.get("solar.uploadInfo", true);
  let link = option.url || "";
  
  if (option.url && option.value) {
    link = option.url.startsWith('http') ? option.url : `https://${_upload?.data?.url}${option.url}`;
  }

  return {
    image: link,
    text: option.name,
    value: option.value,
  }
}

const getOptionImage = (item: OptionWithEstrutura<number> & { grupos: Grupo[] }): string => {
  const grupos = item.grupos || [];
  const grupoIdSelecionado = item.estruturas_grupo_id;
  const grupoSelecionado = grupos.find(item => item.id === grupoIdSelecionado);

  return grupoSelecionado?.url_imagem || item.image || ''
}

export const elSemEstrutura = { exibir_popup_parceiro: false, status: true, posicao: null, estruturas_grupo_id: 0, grupos: [], inversor: '', segmento: '', image: SemEstrutura, url_imagem: SemEstrutura, nome: 'Sem estrutura', text: 'Sem estrutura', id: 0, value: 0 };

export const ModalEditarEstruturas = ({ onClose, open, segmento = 'on' }: ModalEditarEstruturasProps) => {
  const classes = useStyles()
  
  const { fileiras, modulo_selecionado, potencia_desejada_kw, regioes, solucao_calculada, todas_estruturas, estruturas_calculadas } = useProjetosHibridoStore(store => store.state.data)
  const projetosHibridoActions = useProjetosHibridoStore(store => store.actions)
  const [fileirasBackup, setFileirasBackup] = useState<typeof fileiras>([])
  const [alterouPosicao, setAlterouPosicao] = useState<boolean>(false)
  const [limparEstrutura, setLimparEstrutura] = useState<number | boolean>(false)
  const oldEstruturasHibrido = useObservable(oldEstruturasHibrido$);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const FooterButton = isMobile ? Box : DialogActions;

  const moduloFiname = useMemo(()=>{
    return modulo_selecionado?.finame || false;
  },[modulo_selecionado])

  const removeFileira = useCallback((index: number) => {
    const newFileiras = fileiras.filter((_, itemIndex) => itemIndex !== index);
    projetosHibridoActions.changeValue({ fileiras: newFileiras });
  },[fileiras, projetosHibridoActions])

  const novaFileira = useCallback((): FormProjetoHibridoType["fileiras"][0] => {
    return {
      posicao_modulo: POSICAO_MODULO.find(pm => pm.value === 'retrato') || null,
      quantidade_modulos: 0,
      fileiras: 1,
      tipo_estrutura: moduloFiname ? null : { value: 0, text: elSemEstrutura.nome, image: elSemEstrutura.url_imagem, grupos: [] },
    }
  }, [])

  const addFileira = useCallback(() => {
    projetosHibridoActions.changeValue({ fileiras: [...fileiras, novaFileira()] })
  },[fileiras, novaFileira, projetosHibridoActions])


  const changeFileiras = useCallback((index: number, field: keyof FormProjetoHibridoType["fileiras"][0], value: any)=>{
    const newFileiras = fileiras.map((item, itemIndex) => {
      if (index === itemIndex) {
        return { ...item, [field]: value}
      }
      return item;
    });
    projetosHibridoActions.changeValue({ fileiras: newFileiras })
  },[fileiras, projetosHibridoActions])

  useEffect(()=>{
    if (limparEstrutura) {
      fileiras?.forEach((_fileira, index) => {
        if ((index + 1) === limparEstrutura) changeFileiras(index, 'tipo_estrutura', null);
      })
      setLimparEstrutura(false);
    }
  },[limparEstrutura]);

  const estruturasOptions = useCallback((posicaoSelecionada: string) => {
    const optionsInicial = moduloFiname ? [...todas_estruturas] : [elSemEstrutura, ...todas_estruturas]

    const options = optionsInicial.map(item => {
        const findGrupo = item.grupos?.find(g => g.id === item.estruturas_grupo_id) || item.grupos?.[0];
        const itemImageUrl = findGrupo?.url_imagem || item.url_imagem;

        return {
          ...renderOption({ name: item.nome, url: item.id ? itemImageUrl : SemEstrutura, value: item.id }),
          grupos: item.grupos || [],
          posicao: findGrupo?.posicao || item.posicao,
          estruturas_grupo_id: item.estruturas_grupo_id,
          segmento: item.segmento,
          id: item.id,
        }
      })

      if (posicaoSelecionada) {
        return options.filter(item => !item.id ||
          ([posicaoSelecionada, null, 'ambos'].includes(item.posicao) &&
          [segmento, 'todos', null].includes(item.segmento) &&
          item?.grupos?.length > 0 &&
          item.grupos.some(grupo => 
            [posicaoSelecionada, 'ambos', null].includes(grupo.posicao) &&
            [segmento, 'todos', null].includes(grupo.segmento) &&
            grupo.id === item.estruturas_grupo_id
          )
        ))
      }
        
      return options;
  }, [todas_estruturas, segmento, moduloFiname])

  const checkRowsDisabled = useCallback(
    (estruturaId: number) => {
      if (estruturaId === 0) return false;
      const withDisable = todas_estruturas?.find(e => e?.id === estruturaId && e?.grupos?.find((i: Grupo) => !i?.permite_fileiras))
      if (withDisable) return true;
      return false;
    },
    [todas_estruturas],
  )

  const modulosRestantes = useMemo(()=>{
    const totalModulos = fileiras.reduce((acc, item) => {
      const estruturaId = Number(item?.tipo_estrutura?.value)
      if (isNaN(estruturaId)) {
        return acc
      }
      const rowsDisabled = checkRowsDisabled(estruturaId)
      const fileiras = (!rowsDisabled ? item.fileiras : 1)

      return acc + Number(item.quantidade_modulos) * Number(fileiras)
    }, 0)
    const totalModulosSelecionados = modulo_selecionado?.quantidade || 0
    return totalModulosSelecionados - totalModulos
  },[checkRowsDisabled, fileiras, modulo_selecionado?.quantidade])

  const rollbackFileiras = useCallback(()=>{
    projetosHibridoActions.changeValue({ fileiras: fileirasBackup });
  },[fileirasBackup, projetosHibridoActions])
  
  const comparaEstruturas = (antiga: ListagemItem[], atual: ListagemItem[])=>{
    if (antiga?.length !== atual?.length) return false;
    return antiga.every(itemAntigo => {
      const itemAtual = atual.find(a => a.componente_id === itemAntigo.componente_id);
      return itemAtual?.quantidade === itemAntigo?.quantidade;
    });
  }

  const salvarFileiras = useCallback(async ()=>{
    acessouEditarEstruturasHibrido$.next(true);
    const fileirasComEstrutura = fileiras.filter(item => item.tipo_estrutura?.value && Number(item.tipo_estrutura.value) > 0);

    const estruturaResult = fileirasComEstrutura.length ? await calcularEstruturas({
      fileiras: fileirasComEstrutura.map(item => ({
        ...item,
        fileiras: checkRowsDisabled(item.tipo_estrutura?.value || 0) ? 1 : item.fileiras
      })),
      inversor: '',
      moduloId: modulo_selecionado?.componente_id || 0,
      potencia: potencia_desejada_kw,
      regioes: regioes,
    }) : []

    oldEstruturasHibrido$.next(estruturaResult);
    let estruturaSelecionada = todas_estruturas.find(item => item.id === fileiras[0].tipo_estrutura?.value && item?.estruturas_grupo_id === fileiras[0].tipo_estrutura?.estruturas_grupo_id) || null;

    if (!estruturaSelecionada && fileiras[0].tipo_estrutura?.value === 0) {
      estruturaSelecionada = elSemEstrutura;
    }
    
    if (estruturaSelecionada && (alterouPosicao || !estruturaSelecionada?.editada)) {
      estruturaSelecionada = {...estruturaSelecionada, editada: true }
    }

    projetosHibridoActions.changeValue({
      estruturas_calculadas: !comparaEstruturas(oldEstruturasHibrido, estruturaResult) ? estruturaResult : (estruturas_calculadas || []),
    })

    projetosHibridoActions.changeValidationsValue({ fileiras: { status: true, helper: "" } })
    
    onClose();
  },[checkRowsDisabled, fileiras, potencia_desejada_kw, modulo_selecionado?.componente_id, onClose, projetosHibridoActions, regioes, solucao_calculada.inversores, todas_estruturas, alterouPosicao, oldEstruturasHibrido])

  return (
    <Dialog onLoad={() => {
      if (!fileirasBackup.length) setFileirasBackup(fileiras)
    }} open={open} onClose={onClose} maxWidth="xl" className="modal-editar-estruturas">
      <DialogTitle className="editar-estruturas-titulo">Disposição dos módulos e fileiras</DialogTitle>

      <Divider />

      <DialogContent>
      {fileiras.map((item, index, list) => /* const estruturaId = Number(item?.tipo_estrutura?.value)*/ /* const rowsDisabled = checkRowsDisabled(estruturaId)*/ (
        <Box style={!isMobile ? { width: "800px" } : {}}>
          <Box mt={4} key={Number(index)} className="row">
            <Box className="X12 M6" mb={isMobile ? 3 : 0}>
              <SelectWithImageInOption
                id="custom-select-posicao-modulo"
                label="* Posição do módulo"
                onChange={async (d) => {
                  // if (item?.posicao_modulo?.value !== d.value) {
                  //   let atualizarEstrutura = false
                  //   if (d && item.tipo_estrutura) {
                  //     const estrutura: EstruturaListagemResponse | undefined = estruturasOptions.find((e: EstruturaListagemResponse) => e.id === item.tipo_estrutura?.value)
                  //     if (estrutura) {
                  //       if (estrutura.posicao !== d.value) {
                  //         atualizarEstrutura = true
                  //       }
                  //     }
                  //   }

                  //   let _estrutura: any = null

                  //   const _formState = { ...formState }
                  //   const _isFiname = (_formState?.modulosList?.find(item => item.selecionado)?.finame && !["personalizado"]?.includes(_formState?.tipo || "")) || false

                  //   if (item.tipo_estrutura !== null && Number(item.tipo_estrutura.value) > 0) {
                  //     _estrutura = item.tipo_estrutura
                  //   }

                  //   if (!_isFiname) {
                  //     _estrutura = atualizarEstrutura ? {
                  //       image: SemEstrutura,
                  //       text: "Sem estrutura",
                  //       value: "",
                  //     } : null
                  //   } else {
                  //     _estrutura = null
                  //   }

                  //   setTimeout(() => {
                  //     changePosicaoModulo(index, 'posicao_modulo', d, _estrutura)
                  //   }, 600)
                  // }
                  setAlterouPosicao(item?.posicao_modulo?.value !== d?.value);

                  if (item.tipo_estrutura?.estruturas_grupo_id) {
                    setLimparEstrutura(item?.posicao_modulo?.value !== d?.value && Number(index + 1));
                  }

                  changeFileiras(index, 'posicao_modulo', d)
                } }
                value={item.posicao_modulo}
                options={POSICAO_MODULO} />
            </Box>
            <Box className="X12 M6" mb={isMobile ? 1 : 0}>
              <SelectWithImageInOption
                id="custom-select-tipo-estrutura"
                label="* Tipo de estrutura"
                onChange={d => {
                  changeFileiras(index, 'tipo_estrutura', d)
                }}
                value={item.tipo_estrutura ? renderOption({
                  name: item.tipo_estrutura?.text,
                  url: getOptionImage(item.tipo_estrutura),
                  value: item.tipo_estrutura.value
                }) : null}
                options={estruturasOptions(item.posicao_modulo?.value || '')}
              />
            </Box>
          </Box>

          <Box className="row">
            <Box className="X12 M6">
              <InputDecimal3
                label={`* Módulos em sequência`}
                margin="normal"
                decimal={0}
                placeholder="0"
                allowNegative={false}
                value={item.quantidade_modulos || ''}
                onChange={(event: any) => {
                  changeFileiras(index, 'quantidade_modulos', event.target.value)
                  // popupParceiroFeleira(index, event.target.value)
                } } />
            </Box>
            <Box className="X12 M6">
              {!checkRowsDisabled(item.tipo_estrutura?.value || 0) && (
                <InputDecimal3
                  label={`* Fileiras iguais`}
                  margin="normal"
                  decimal={0}
                  placeholder="0"
                  allowNegative={false}
                  value={!checkRowsDisabled(item.tipo_estrutura?.value || 0) ? (item.fileiras || '') : 1}
                  onChange={(event: any) => {
                    changeFileiras(index, 'fileiras', event.target.value)
                    // popupParceiroFeleira(index, event.target.value)
                  } } />
              )} 
            </Box>
          </Box>

          {list.length > 1 && (
            <Box display="flex" justifyContent={"flex-end"} mt={isMobile ? 1 : 0} mb={isMobile ? 1 : 0}>
              <Box width={!isMobile ? "auto" : "100%"}>
                <Button variant="outlined" onClick={() => removeFileira(index)} className={classes.removeFileiraButton} disableElevation startIcon={<Delete />} fullWidth={isMobile}>
                  Excluir Fileira
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      ))}

      {modulosRestantes !== 0 && (
        <small style={{ color: theme.palette.error.main, textAlign: 'start', display: 'block', marginTop: 5 }}>{HIBRIDO_ERROR_MESSAGES.FILEIRAS_INVALIDAS}</small>
      )}

      <Box mt={2} display="flex" justifyContent={"flex-end"} flexDirection="column" alignItems={"flex-end"}>
        <Box my={2} width={!isMobile ? "auto" : "100%"}>
          <Tooltip title="Fique atento ao campo “módulos restantes” para estruturar o gerador. Não é possível adicionar fileiras quando não houverem mais módulos restantes.">
            <Button disabled={modulosRestantes <= 0} onClick={addFileira} className={classes.addFileiraButton} variant="contained" disableElevation startIcon={<Add />} fullWidth={isMobile}>
              Adicionar Fileira
            </Button>
          </Tooltip>
        </Box>
      </Box>

      </DialogContent>

      <FooterButton className="editar-estruturas-footer">
        <Button onClick={() => {
          rollbackFileiras()
          onClose()
        }} color="secondary" variant={isMobile ? 'outlined' : 'text'} fullWidth={isMobile}>
          Cancelar
        </Button>
        <Button color="primary" variant="contained" disabled={modulosRestantes !== 0} disableElevation onClick={salvarFileiras} fullWidth={isMobile}>
          Salvar
        </Button>
      </FooterButton>
    </Dialog>
  )
}